/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Dashboard\ActionsCommerciales.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ActionCommerciale } from '../../services/DashboardService';

interface ActionsCommercialesProps {
  actions: ActionCommerciale[];
}

const ActionsCommerciales: React.FC<ActionsCommercialesProps> = ({ actions }) => {
  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant);
  };

  const getUrgenceColor = (urgence: string) => {
    switch (urgence) {
      case 'haute':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'moyenne':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'faible':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'relance':
        return (
          <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'livraison':
        return (
          <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        );
      case 'finalisation':
        return (
          <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getActionLabel = (type: string) => {
    switch (type) {
      case 'relance':
        return 'À relancer';
      case 'livraison':
        return 'À livrer';
      case 'finalisation':
        return 'À finaliser';
      default:
        return type;
    }
  };

  if (actions.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="mx-auto h-12 w-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune action requise</h3>
        <p className="mt-1 text-sm text-gray-500">Tous vos devis sont à jour !</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {actions.slice(0, 8).map((action, index) => (
        <div
          key={`${action.devisId}-${index}`}
          className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {getActionIcon(action.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {action.reference}
                </p>
                <span 
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getUrgenceColor(action.urgence)}`}
                  title={`Urgence ${action.urgence} - ${action.joursAttente} jour${action.joursAttente > 1 ? 's' : ''} d'attente`}
                >
                  {action.urgence}
                </span>
              </div>
              <p className="text-sm text-gray-500 truncate">
                {action.clientNom} • {formatMontant(action.montant)}
              </p>
              <p className="text-xs text-gray-400">
                {getActionLabel(action.type)} • {action.joursAttente} jour{action.joursAttente > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex-shrink-0">
            <Link
              to={`/devis/configurateur/${action.devisId}`}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
            >
              Voir
            </Link>
          </div>
        </div>
      ))}
      
      {actions.length > 8 && (
        <div className="text-center pt-2">
          <Link
            to="/devis"
            className="text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            Voir toutes les actions ({actions.length})
          </Link>
        </div>
      )}
    </div>
  );
};

export default ActionsCommerciales;
